/**
 * `metadata.allowManualPaymentForPrices` on a checkout variant is either `["all"]` or a list of
 * price ids that may be paid by bank transfer. The form shows it as a switch ("all") plus a price
 * picker; a per-price list must survive a save, or carts that allow bank transfer for one price
 * only lose it.
 */
export const ALL_PRICES_MANUAL = "all";

export interface ManualPaymentFormValues {
  allowManualPayment: boolean;
  manualPaymentPrices: string[];
}

export const toManualPaymentFormValues = (
  allowManualPaymentForPrices: readonly string[] | null | undefined,
): ManualPaymentFormValues => {
  const list = allowManualPaymentForPrices ?? [];

  if (list.includes(ALL_PRICES_MANUAL)) {
    return { allowManualPayment: true, manualPaymentPrices: [] };
  }

  return { allowManualPayment: false, manualPaymentPrices: [...list] };
};

/**
 * Only prices the variant still sells are kept, so a price removed from the cart does not stay
 * allowed for bank transfer once it is added back.
 */
export const toAllowManualPaymentForPrices = (
  values: Partial<ManualPaymentFormValues> | undefined,
  variantPriceIds: readonly string[],
): string[] => {
  if (values?.allowManualPayment) {
    return [ALL_PRICES_MANUAL];
  }

  const sold = new Set(variantPriceIds);

  return [...new Set(values?.manualPaymentPrices ?? [])].filter((priceId) => sold.has(priceId));
};
